<template>
  <div>
    <b-alert
      dismissible
      class="mb-1"
      :show="user && user.authenticated && !user.confirmed_at"
    >
      <div class="text-center">
        <span class="text-green"
          >{{ $t("general.verify_email") }}
          <resend-activation-link class="font-weight-600 ml-1"
        /></span>
      </div>
    </b-alert>
    <div v-if="accreditationAlertVisible">
      <b-alert
        dismissible
        class="mb-1"
        :variant="accreditationAlertVariant"
        show
      >
        <div class="text-center">
          <span class="text-green">
            {{ accreditationAlertMessage }}
            <router-link
              v-if="accreditationAlertRequiresUpload"
              class="font-weight-600 ml-1"
              to="/dashboard"
            >
              Upload
            </router-link>
          </span>
        </div>
      </b-alert>
    </div>

    <div v-if="!accreditationAlertVisible && !shouldShowAccreditedVerify">
      <b-alert
        dismissible
        class="mb-1"
        :show="shouldShowAccreditedDocsReminder"
      >
        <div class="text-center">
          <span class="text-green"
            >Please <router-link class='font-weight-600' to='/dashboard'>upload</router-link> your accredited investor docs for your recent investment
          </span>
        </div>
      </b-alert>
    </div>
    <b-alert
      dismissible
      class="mb-1"
      :show="photoIdAlertVisible"
    >
      <div class="text-center">
        <span class="text-green">
          {{ photoIdAlertMessage }}
          <upload-photo-id-link
            v-if="photoIdAlertRequiresUpload"
            class="font-weight-600 ml-1"
          />
        </span>
      </div>
    </b-alert>
      <b-alert
      dismissible
      class="mb-1"
      :show="isStatusApproved"
    >
    <div class="text-center">
        <span class="text-green"
          >Based on your documents, you have been verified as an <b>accredited</b> investor.
          </span>
      </div>
    </b-alert>
    <b-container class="" style="position: relative" fluid>
      <div
        style="
          position: absolute;
          left: 50%;
          transform: translate(-50%, 0);
          z-index: 9999;
          opacity: 0.88;
        "
      >
        <template v-for="alert in $store.getters['alerts']">
          <b-alert
            dismissible
            v-if="alert.error && alert.error.code === 5"
            :key="alert.message"
            variant="warning"
            v-model="alert.show"
            class="mb-1"
          >
            Confirm your email first.
            <resend-activation-link />
          </b-alert>
          <b-alert
            v-model="alert.show"
            v-else
            :variant="alert_variant(alert)"
            dismissible
            :key="alert.message"
            class="mb-1"
          >
            {{ alert.message }}
            <span v-if="alert.count && alert.count > 1"
              >({{ alert.count }})</span
            >
          </b-alert>
        </template>
      </div>
    </b-container>
  </div>
</template>

<script>
import ResendActivationLink from "@/components/ResendActivationLink.vue";
import UploadPhotoIdLink from "@/components/UploadPhotoIdLink.vue";
import backend from "@/services/backend";
import {
  normalizedPhotoIdStatusFromUser,
  photoIdStatePatchFromPayload,
} from "@/services/photo-id-state";
import { isExpectedAxiosValidationError } from "@/utils/sentryApiErrors";

export default {
  components: { ResendActivationLink, UploadPhotoIdLink },
  data: () => {
    return {
      show_modal: true,
      additionMessage: '',
      shouldShowAccreditedVerify: false
    };
  },

  methods: {
    alert_variant(alert) {
      if (alert.type === "error") return "danger";
      else if (alert.type === "success") return "success";
      return "info";
    },
    close_modal() {
      this.show_modal = false;
      this.$store.state.showConfirmModal = false;
    },
    showInvestorAccreditationAlert() {
      if (this.$route && this.$route.path && this.$route.path.startsWith("/admin")) {
        this.shouldShowAccreditedVerify = false;
        return false;
      }
      if (!this.user || !this.user.authenticated) {
        this.shouldShowAccreditedVerify = false;
        return false;
      }
      if (this.accreditationStatus) {
        this.additionMessage = (
          this.user.accreditation_verification_message || ""
        );
        this.shouldShowAccreditedVerify = this.accreditationAlertVisible;
        return this.shouldShowAccreditedVerify;
      }
      if (!this.user.nc_accredited_id) {
        this.shouldShowAccreditedVerify = false;
        return false;
      }
      return backend
        .get_accreditation()
        .then((res) => {
          if (res.data.aiRequestStatus === "Need More Info") {
            this.additionMessage = res.data.notes;
            this.shouldShowAccreditedVerify = true;
            return true;
          } else {
            this.shouldShowAccreditedVerify = false;
            return false;
          }
        })
        .catch((error) => {
          if (isExpectedAxiosValidationError(error)) {
            this.shouldShowAccreditedVerify = false;
            return false;
          }
          console.error("An error occurred while fetching accreditation:", error);
          this.shouldShowAccreditedVerify = false;
          return false;
        });
    },
    refreshIdentityVerification() {
      if (!this.user || !this.user.authenticated) return Promise.resolve(null);
      return backend.get_identity_verification()
        .then((response) => {
          this.commitPhotoIdState(response && response.data);
          return response;
        })
        .catch((error) => {
          if (error && error.response && error.response.status === 401) {
            return null;
          }
          // eslint-disable-next-line no-console
          console.error("An error occurred while fetching identity verification:", error);
          return null;
        });
    },
    refreshMe() {
      return backend.me()
        .then((response) => {
          if (response && response.data) {
            this.$store.commit("user/set_user", response.data);
          }
          return response;
        })
        .catch((error) => {
          if (error && error.response && error.response.status === 401) {
            return null;
          }
          // eslint-disable-next-line no-console
          console.error("An error occurred while refreshing user session:", error);
          return null;
        });
    },
    commitPhotoIdState(payload) {
      const patch = photoIdStatePatchFromPayload(payload || {});
      if (Object.keys(patch).length > 0) {
        this.$store.commit("user/set_user_data", patch);
      }
    },
    refreshUserAndIdentityState() {
      return this.refreshMe().then((response) => {
        const refreshedUser = response && response.data ? response.data : this.user;
        if (!refreshedUser || !refreshedUser.authenticated) {
          return response;
        }
        return this.refreshIdentityVerification().then(() => this.refreshMe());
      });
    },
  },
  beforeMount() {
    this.showInvestorAccreditationAlert(); // The $refs object is empty at this point
    this.refreshUserAndIdentityState();
  },

  created() {
    this.showInvestorAccreditationAlert();
  },
  watch: {
    user(newUser) {
      if (newUser && newUser.nc_accredited_id) {
        this.showInvestorAccreditationAlert();
      }
    },
  },
  computed: {
    user() {
      const user = this.$store.getters["user/user"];
      // console.log("User data:", user); // Debugging log
      return user; 
    },
    photoIdStatus() {
      return normalizedPhotoIdStatusFromUser(this.user);
    },
    accreditationStatus() {
      return this.user && this.user.accreditation_verification_status;
    },
    // Single source for "this investor has an unresolved per-issuer
    // accreditation state". Both the under-review alert AND the verified alert
    // read it, so they cannot contradict each other -- which is exactly what
    // happened: `isStatusApproved` consulted only the legacy global
    // `nc_accredited_status`, so an investor with a PENDING per-issuer
    // verification was shown "your documents are under review" and "you have
    // been verified as an accredited investor" side by side.
    accreditationOpenState() {
      return [
        "document_required",
        "review_in_progress",
        "additional_info_needed",
        "ai_cleared_pending_audit",
        "held_for_next_close",
        "rejected",
        "expired_or_not_reusable",
        "manual_compliance_blocked",
      ].includes(this.accreditationStatus);
    },
    accreditationAlertVisible() {
      if (!this.user || !this.user.authenticated) return false;
      if (
        this.$route &&
        this.$route.path &&
        this.$route.path.startsWith("/admin")
      ) {
        return false;
      }
      return this.accreditationOpenState;
    },
    accreditationAlertRequiresUpload() {
      return [
        "document_required",
        "additional_info_needed",
        "expired_or_not_reusable",
      ].includes(this.accreditationStatus);
    },
    accreditationAlertVariant() {
      if (
        ["rejected", "manual_compliance_blocked"].includes(
          this.accreditationStatus,
        )
      ) {
        return "danger";
      }
      if (
        [
          "review_in_progress",
          "ai_cleared_pending_audit",
          "held_for_next_close",
        ].includes(this.accreditationStatus)
      ) {
        return "info";
      }
      return "warning";
    },
    accreditationAlertMessage() {
      if (this.user && this.user.accreditation_verification_message) {
        return this.user.accreditation_verification_message;
      }
      if (this.accreditationStatus === "document_required") {
        return "Accreditation documentation is required for your 506(c) investment.";
      }
      if (this.accreditationStatus === "review_in_progress") {
        return "Your accreditation documents are under review.";
      }
      if (this.accreditationStatus === "additional_info_needed") {
        return "Additional accreditation documentation is needed.";
      }
      if (this.accreditationStatus === "ai_cleared_pending_audit") {
        return "Your accreditation documents are under human review.";
      }
      if (this.accreditationStatus === "held_for_next_close") {
        return "Your funds remain in escrow pending accreditation review.";
      }
      if (this.accreditationStatus === "rejected") {
        return "Your accreditation could not be verified.";
      }
      if (this.accreditationStatus === "expired_or_not_reusable") {
        return "Please upload current accreditation documentation.";
      }
      if (this.accreditationStatus === "manual_compliance_blocked") {
        return "Your accreditation review is blocked pending compliance review.";
      }
      return "";
    },
    photoIdAlertVisible() {
      return (
        this.user &&
        this.user.authenticated &&
        this.user.kyc_check !== true &&
        ["required", "replacement_required", "pending_review"].includes(
          this.photoIdStatus,
        )
      );
    },
    photoIdAlertRequiresUpload() {
      return ["required", "replacement_required"].includes(this.photoIdStatus);
    },
    photoIdAlertMessage() {
      if (this.photoIdStatus === "pending_review") {
        return (
          "Your photo ID has been received and is pending review. " +
          "We'll email you when verification is complete, usually within 1-2 business days."
        );
      }
      if (this.photoIdStatus === "replacement_required") {
        return "We need a replacement photo ID to complete identity verification.";
      }
      return this.$t("general.upload_photo_id");
    },
    investments() {
      return this.$store.getters["user/portfolio"].investments;
    },
    shouldShowAccreditedDocsReminder() {
      if (!this.user) return;
      // console.log(this.user);
      return (
        this.user.accredited === true &&
        this.user.accredited_document_uploaded === false &&
        this.user.nc_accredited_id !== null
      );
    },
    isStatusApproved() {
      // "You have been verified as an accredited investor" is a compliance
      // statement. Under Rule 506(c) the per-issuer verification is the
      // authority, so it must not be asserted while one is still open -- the
      // legacy global field alone is not sufficient grounds.
      if (this.accreditationOpenState) {
        return false;
      }
      return !!(this.user && this.user.nc_accredited_status == "Approved");
    }
    // showInvestorAccreditationAlert() {
    //       if (!this.user || !this.user.nc_accredited_id) {
    //         this.shouldShowAccreditedVerify = false;
    //         return false;
    //       }
    //       return backend
    //         .get_accreditation()
    //         .then((res) => {
    //           if (res.data.aiRequestStatus === "Need More Info") {
    //             this.shouldShowAccreditedVerify = true;
    //             return true;
    //           } else {
    //             this.shouldShowAccreditedVerify = false;
    //             return false;
    //           }
    //         })
    //         .catch((error) => {
    //           console.error("An error occurred while fetching accreditation:", error);
    //           this.shouldShowAccreditedVerify = false;
    //           return false;
    //         });
    //     },
  },
};
</script>

<style>
.fixed-alerts {
  position: fixed;
  top: 0px;
  left: 0px;
  width: 100%;
  z-index: 9999;
  border-radius: 0px;
}

.modal-body-5 {
  margin-top: 40vh !important;
  z-index: 99999 !important;
  left: 0px !important;
}

.rounded-xxl {
  border-radius: 20px;
}

.modal-wrapper-5 {
  position: fixed !important;
  z-index: 999999 !important;
  top: 0px !important;
  height: 100vh !important;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.37);
  overflow: auto !important;
}

.close-modal-icon-5 {
  position: absolute !important;
  right: -15px !important;
  top: 0px;
  z-index: 99 !important;
}

@media (min-width: 1000px) {
  .modal-wrapper-5 {
    display: flex;
    justify-content: center;
    align-items: center !important;
  }
  .modal-body-5 {
    width: 44%;
    margin-top: 0vh !important;
  }
  .close-modal-icon-5 {
    right: 15px;
    top: 10px;
  }
}
</style>
